import React, { useMemo, useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Checkbox, Spinner } from "@chakra-ui/react";
import { CleanFiltersIcon } from "~misc/icons";
import Select from "~components/Form/components/Select";
import "./style.scss";
import useGrupoProduto from "~/fetchs/useGrupoProdutoV2";
import useProduto from "~/fetchs/useProdutoV2";
import useFilial from "~/fetchs/useFilialV2";
import useTipoNegociacao from "~/fetchs/useTipoNegociacaoV2";
import useClientesOptions from "~/fetchs/useClientesOptionsV2";
import usePrecoRegraV2 from "~fetchs/usePrecoRegraV2";
import { getStoragekeysNegociations } from "~pages/PricesTableV2/helpers/storage";
import { useFormContext, Controller } from "react-hook-form";
import useTabelaSolicitacoesOptions from "~fetchs/useTabelaSolicitacoesOptionsV2";
import CustomSelectRangePicker from "~components/Form/components/CustomSelectRangePicker";

const FiltroNegociacoesESolicitacoes = ({
  hideSelectAll,
  handleSelectAll,
  clientesDisponiveis,
  nomeDoFiltro,
  filters,
  loadingx,
  solicitacao,
}) => {
  const [loadingClientOptions, setLoadingClientOptions] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const pickerRef = useRef();
  const { watch, control, reset } = useFormContext();

  const watchProduto = watch("produto");
  const watchGrupoProduto = watch("grupoProduto");

  const { options } = useClientesOptions({
    type: "options",
    setLoading: setLoadingClientOptions,
    clientesDisponiveis,
  });

  const { filtrosDisponiveisPrecoRegra: filtrosDisponiveis } = usePrecoRegraV2({
    filters,
    setLoading: setLoadingFilters,
  });

  const { grupoMaterialDisponiveis, produtosDisponiveis, filiaisDisponiveis } = filtrosDisponiveis;

  const optionDefault = useMemo(() => ({ label: "Todos", value: 0 }), []);
  const optionDefaultLoading = useMemo(
    () => ({
      label: loadingFilters ? "Carregando..." : optionDefault.label,
      value: optionDefault.value,
    }),
    [loadingFilters, optionDefault],
  );

  const keys = useMemo(() => getStoragekeysNegociations(nomeDoFiltro), [nomeDoFiltro]);

  const { optionGruposProduto } = useGrupoProduto({
    camposOpt: ["nome"],
    gruposDisponiveis: grupoMaterialDisponiveis,
  });

  const { optionProdutos } = useProduto({
    grupoProduto: watchGrupoProduto,
    produtosDisponiveis: produtosDisponiveis,
    produto: watchProduto,
  });

  const { optionFiliais } = useFilial({
    filiaisDisponiveis,
  });

  const { optionNegociacoes } = useTipoNegociacao();

  const optionsOrderDefault = useMemo(
    () => [
      { label: "Enviados Recentemente", value: "antigos" },
      { label: "Data de Vigência", value: "recentes" },
      { label: "Ordem Alfabética", value: "ordem-alfabetica" },
      { label: "Ordem Crescente", value: "ordem-crescente" },
      { label: "Ordem Decrescente", value: "ordem-decrescente" },
    ],
    [],
  );

  const optionsOrderSolicitations = useMemo(
    () => [
      { label: "Recentes", value: "recentes" },
      { label: "Antigos", value: "antigos" },
    ],
    [],
  );

  const optionsOrder = useMemo(
    () => (solicitacao ? optionsOrderSolicitations : optionsOrderDefault),
    [optionsOrderSolicitations, optionsOrderDefault, solicitacao],
  );

  const { optionSolicitacoes } = useTabelaSolicitacoesOptions();

  const optionsStatus = [
    { label: "Todos", value: -1 },
    { label: "Aprovado", value: "aprovado" },
    { label: "Não enviado", value: "nao_enviado" },
    { label: "Pendente", value: "pendente" },
    { label: "Recusado", value: "recusado" },
  ];

  const [showDatePicker, setShowDatePicker] = useState(false);

  const watchPeriodo = watch("periodo");

  useEffect(() => {
    if (watchPeriodo?.value === "personalizado") {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
    }
  }, [watchPeriodo?.value]);

  const clear = () => {
    Object.values(keys).forEach((key) => {
      localStorage.removeItem(key);
    });

    reset({
      grupoProduto: 0,
      produto: 0,
      filial: 0,
      tipoSolicitacao: 0,
      ordem: "recentes",
      cliente: 0,
      statusSolicitacao: -1,
      periodo: { value: 0, label: "Todos", startDate: null, endDate: null },
    });
  };

  const optTipoNegociacao = useMemo(() => [optionDefault, ...optionNegociacoes], [optionDefault, optionNegociacoes]);
  const optGruposProduto = useMemo(
    () => [optionDefaultLoading, ...optionGruposProduto],
    [optionDefaultLoading, optionGruposProduto],
  );
  const optTabelaSolicitacoes = useMemo(
    () => (options ? [optionDefault, ...optionSolicitacoes] : []),
    [optionDefault, options, optionSolicitacoes],
  );
  const optProdutos = useMemo(() => [optionDefaultLoading, ...optionProdutos], [optionDefaultLoading, optionProdutos]);
  const optFiliais = useMemo(() => [optionDefaultLoading, ...optionFiliais], [optionDefaultLoading, optionFiliais]);
  const optClientes = useMemo(() => (options ? [optionDefault, ...options] : []), [optionDefault, options]);
  const optionsClientes = useMemo(
    () => (solicitacao ? optTabelaSolicitacoes : optClientes),
    [solicitacao, optTabelaSolicitacoes, optClientes],
  );

  return (
    <>
      <section className="container prices-table-filter">
        <div className="form grid grid-cols-3 gap-x-3">
          <Controller
            name="cliente"
            control={control}
            render={({ field }) => (
              <Select
                label="Cliente:"
                options={optionsClientes}
                spacingControl={true}
                isDisabled={loadingClientOptions}
                loading={loadingClientOptions}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption.value);
                  localStorage.setItem(keys.cliente, JSON.stringify(selectedOption));
                }}
                value={optionsClientes?.find((option) => option.value === field.value) || 0}
              />
            )}
          />

          <Controller
            name="grupoProduto"
            control={control}
            render={({ field }) => (
              <Select
                label="Grupo de Produto:"
                options={optGruposProduto}
                spacingControl={true}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption.value);
                  localStorage.setItem(keys.grupoProduto, JSON.stringify(selectedOption));
                }}
                isDisabled={loadingFilters}
                loading={loadingFilters}
                value={optGruposProduto?.find((option) => option.value === field.value) || 0}
              />
            )}
          />

          <Controller
            name="produto"
            control={control}
            render={({ field }) => (
              <Select
                label="Produtos:"
                options={optProdutos}
                spacingControl={true}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption.value);
                  localStorage.setItem(keys.produto, JSON.stringify(selectedOption));
                }}
                isDisabled={loadingFilters}
                loading={loadingFilters}
                value={optProdutos?.find((option) => option.value === field.value) || 0}
              />
            )}
          />

          <Controller
            name="filial"
            control={control}
            render={({ field }) => (
              <Select
                label="Centro:"
                options={optFiliais}
                spacingControl={true}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption.value);
                  localStorage.setItem(keys.filiais, JSON.stringify(selectedOption));
                }}
                isDisabled={loadingFilters}
                loading={loadingFilters}
                value={optFiliais?.find((option) => option.value === field.value) || 0}
              />
            )}
          />

          <Controller
            name="tipoSolicitacao"
            control={control}
            render={({ field }) => (
              <Select
                label="Tipo de Negociação:"
                spacingControl={true}
                options={optTipoNegociacao}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption.value);
                  localStorage.setItem(keys.negociacao, JSON.stringify(selectedOption));
                }}
                value={optTipoNegociacao?.find((option) => option.value === field.value) || 0}
              />
            )}
          />

          {solicitacao && (
            <>
              <Controller
                name="statusSolicitacao"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Status de Solicitação:"
                    options={optionsStatus}
                    spacingControl={true}
                    onChange={(selectedOption) => {
                      field.onChange(selectedOption.value);
                      localStorage.setItem(keys.status, JSON.stringify(selectedOption));
                    }}
                    value={optionsStatus?.find((option) => option.value === field.value) || -1}
                  />
                )}
              />
              <Controller
                name="periodo"
                control={control}
                defaultValue={{ value: 0, label: "Todos" }}
                render={({ field }) => (
                  <CustomSelectRangePicker
                    ref={pickerRef}
                    selectedOption={field.value}
                    showDatePicker={showDatePicker}
                    setShowDatePicker={setShowDatePicker}
                    handleSelectChange={(selectedOption) => {
                      const newValue = {
                        ...selectedOption,
                        startDate: selectedOption.value === "personalizado" ? field.value?.startDate : null,
                        endDate: selectedOption.value === "personalizado" ? field.value?.endDate : null,
                      };
                      field.onChange(newValue);
                      localStorage.setItem(keys.periodo, JSON.stringify(newValue));

                      // Controla a exibição do datepicker
                      if (selectedOption.value === "personalizado") {
                        setShowDatePicker(true);
                      } else {
                        setShowDatePicker(false);
                      }
                    }}
                    onDateChange={(dates) => {
                      const [start, end] = dates;
                      const newValue = {
                        ...field.value,
                        startDate: start,
                        endDate: end,
                      };
                      field.onChange(newValue);
                      localStorage.setItem(keys.periodo, JSON.stringify(newValue));
                    }}
                    startDate={field.value?.startDate}
                    endDate={field.value?.endDate}
                    clearSelectRange={() => {
                      const defaultValue = { value: 0, label: "Todos", startDate: null, endDate: null };
                      field.onChange(defaultValue);
                      setShowDatePicker(false);
                      localStorage.setItem(keys.periodo, JSON.stringify(defaultValue));
                    }}
                  />
                )}
              />
            </>
          )}

          <div className="col-span-3 flex ml-auto gap-4">
            {!hideSelectAll && (
              <div className="checkbox-container">
                <label className="checkbox flex items-center" data-test-id="checkbox-selecionar-todas">
                  <span className="desc ml-0 mr-2">Selecionar todas</span>
                  <Checkbox onChange={handleSelectAll} />
                </label>
              </div>
            )}

            <button type="button" className="clear-filter cursor-pointer" onClick={clear}>
              <span className="desc ml-0 mr-2 cursor-pointer">Limpar Filtros</span>
              <CleanFiltersIcon />
            </button>

            <div className="flex ml-1 ">
              <Controller
                name="ordem"
                control={control}
                render={({ field }) => {
                  // Se não achar, seta a opção 1 do array (recentes)
                  const selectedOption = optionsOrder.find((option) => option.value === field.value) ||
                    optionsOrder[0] || { label: "Recentes", value: "recentes" };
                  return (
                    <Select
                      className="no-margin"
                      variation="compact-inline"
                      label="Ordenar por:"
                      options={optionsOrder}
                      spacingControl={false}
                      onChange={(selectedOption) => {
                        field.onChange(selectedOption.value);
                      }}
                      value={selectedOption + `aaaaaaaaaaaaaaaa`}
                    />
                  );
                }}
              />
            </div>
          </div>
        </div>
      </section>
      <div className="container">
        <div className="flex">
          {loadingx && (
            <p className="flex items-start gap-2 text-sm mt-4">
              <Spinner size={"sm"} />
              Verificando se há atualizações nas requisições
            </p>
          )}
        </div>
      </div>
    </>
  );
};

FiltroNegociacoesESolicitacoes.propTypes = {
  hideSelectAll: PropTypes.bool,
  handleSelectAll: PropTypes.func,
  clientesDisponiveis: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string])),
  nomeDoFiltro: PropTypes.string.isRequired,
  filters: PropTypes.object.isRequired,
  loadingx: PropTypes.bool,
  solicitacao: PropTypes.bool,
};

export default FiltroNegociacoesESolicitacoes;
